import { Cloud } from 'lucide-react';

import Kube from '@/assets/ico/kube.svg?c';
import { isVersionSmallerOrEqual } from '@/react/common/semver-utils';
import PodmanIcon from '@/assets/ico/vendor/podman-icon.svg?c';
import DockerIcon from '@/assets/ico/vendor/docker-icon.svg?c';
import DockerSwarmIcon from '@/assets/ico/vendor/docker-swarm-icon.svg?c';
import MicrosoftIcon from '@/assets/ico/vendor/microsoft-icon.svg?c';

import {
  Environment,
  EnvironmentStatus,
  EnvironmentType,
  ContainerEngine,
  PlatformType,
} from '../types';

export function getPlatformType(
  envType?: EnvironmentType,
  containerEngine?: ContainerEngine
) {
  switch (envType) {
    case EnvironmentType.KubernetesLocal:
    case EnvironmentType.AgentOnKubernetes:
    case EnvironmentType.EdgeAgentOnKubernetes:
      return PlatformType.Kubernetes;
    case EnvironmentType.Docker:
    case EnvironmentType.AgentOnDocker:
    case EnvironmentType.EdgeAgentOnDocker:
      if (containerEngine === ContainerEngine.Podman) {
        return PlatformType.Podman;
      }
      return PlatformType.Docker;
    case EnvironmentType.Azure:
      return PlatformType.Azure;
    case undefined:
    default:
      throw new Error(`Environment type ${envType} is not supported`);
  }
}

export function isDockerEnvironment(envType: EnvironmentType) {
  return getPlatformType(envType) === PlatformType.Docker;
}

export function isKubernetesEnvironment(envType: EnvironmentType) {
  return getPlatformType(envType) === PlatformType.Kubernetes;
}

// The first agent release that forwards Docker API requests to d2k; -0
// admits its pre-releases, as the server does.
const D2K_MINIMUM_AGENT_VERSION = '3.0.0-0';

// The status summary the server gives a D2K environment whose Kubernetes
// environment was deleted.
const D2K_PARENT_DELETED_SUMMARY = 'Kubernetes environment deleted';

/**
 * getD2KUnsupportedReason returns why D2K environments can't run in the
 * environment, if they can't: it isn't a Kubernetes environment with an Edge
 * agent, or its Edge agent can't forward Docker API requests to d2k.
 */
export function getD2KUnsupportedReason(
  environment: Pick<Environment, 'Type' | 'Agent' | 'Edge'>
) {
  if (environment.Type !== EnvironmentType.EdgeAgentOnKubernetes) {
    return 'D2K environments run in Kubernetes environments with an Edge agent.';
  }

  if (environment.Edge?.AsyncMode) {
    return "D2K needs an Edge agent connected through a tunnel; this environment's Edge agent runs in async mode.";
  }

  const version = environment.Agent?.Version;
  if (version && isVersionSmallerOrEqual(D2K_MINIMUM_AGENT_VERSION, version)) {
    return undefined;
  }

  return `D2K needs Portainer agent 3.0.0 or later; this environment's agent is ${version || 'of an unknown version'}.`;
}

/**
 * isD2KEnvironment returns true for a D2K environment: a Docker environment
 * whose Docker API is served by d2k, in a namespace of a Kubernetes
 * environment.
 */
export function isD2KEnvironment(
  environment?: Pick<Environment, 'ContainerEngine'> | null
) {
  return environment?.ContainerEngine === ContainerEngine.D2K;
}

/**
 * isD2KSwarm returns true for a D2K environment whose d2k runs in Swarm mode.
 */
export function isD2KSwarm(
  environment?: Pick<Environment, 'ContainerEngine' | 'D2K'> | null
) {
  return isD2KEnvironment(environment) && !!environment?.D2K?.SwarmMode;
}

/**
 * isD2KParentDeleted returns true for a D2K environment whose Kubernetes
 * environment was deleted: Portainer has no way left to reach its d2k.
 */
export function isD2KParentDeleted(
  environment: Pick<Environment, 'ContainerEngine' | 'Status' | 'StatusMessage'>
) {
  return (
    isD2KEnvironment(environment) &&
    environment.Status === EnvironmentStatus.Down &&
    environment.StatusMessage?.summary === D2K_PARENT_DELETED_SUMMARY
  );
}

export function getPlatformTypeName(
  envType: EnvironmentType,
  containerEngine?: ContainerEngine
): string {
  if (containerEngine === ContainerEngine.D2K) {
    return 'D2K';
  }

  return PlatformType[getPlatformType(envType, containerEngine)];
}

export function isSnapshotBrowsingSupported(environment: Environment) {
  return isDockerEnvironment(environment.Type) && isEdgeAsync(environment);
}

export function isAgentEnvironment(envType: EnvironmentType) {
  return (
    isEdgeEnvironment(envType) ||
    [EnvironmentType.AgentOnDocker, EnvironmentType.AgentOnKubernetes].includes(
      envType
    )
  );
}

export function isEdgeEnvironment(envType: EnvironmentType) {
  return [
    EnvironmentType.EdgeAgentOnDocker,
    EnvironmentType.EdgeAgentOnKubernetes,
  ].includes(envType);
}

export function isEdgeAsync(env?: Environment | null) {
  return !!env && env.Edge.AsyncMode;
}

export function isUnassociatedEdgeEnvironment(env: Environment) {
  return isEdgeEnvironment(env.Type) && !env.EdgeID;
}

export function isLocalEnvironment(environment: Environment) {
  return (
    isLocalDockerEnvironment(environment.URL) ||
    environment.Type === EnvironmentType.KubernetesLocal
  );
}

export function isLocalDockerEnvironment(environmentUrl: string) {
  return (
    environmentUrl.startsWith('unix://') ||
    environmentUrl.startsWith('npipe://')
  );
}

export function isDockerAPIEnvironment(environment: Environment) {
  return (
    environment.URL.startsWith('tcp://') &&
    environment.Type === EnvironmentType.Docker
  );
}

export function isAzureEnvironment(envType: EnvironmentType) {
  return envType === EnvironmentType.Azure;
}

export function getDashboardRoute(environment: Environment) {
  if (isEdgeEnvironment(environment.Type)) {
    if (!environment.EdgeID) {
      return {
        to: 'portainer.endpoints.endpoint',
        params: { id: environment.Id },
      };
    }

    if (isEdgeAsync(environment)) {
      if (!isSnapshotBrowsingSupported(environment)) {
        return {
          to: '',
        };
      }

      return {
        to: 'edge.browse.dashboard',
        params: { endpointId: environment.Id },
      };
    }
  }

  const params = { endpointId: environment.Id };
  const to = getPlatformRoute();

  return { to, params };

  function getPlatformRoute() {
    const platform = getPlatformType(environment.Type);
    switch (platform) {
      case PlatformType.Azure:
        return 'azure.dashboard';
      case PlatformType.Docker:
        return 'docker.dashboard';
      case PlatformType.Kubernetes:
        return 'kubernetes.dashboard';
      case PlatformType.Podman:
      default:
        throw new Error(`Unsupported platform ${platform}`);
    }
  }
}

export function getEnvironmentTypeIcon(
  type: EnvironmentType,
  containerEngine?: ContainerEngine,
  swarm = false
) {
  switch (type) {
    case EnvironmentType.Azure:
      return MicrosoftIcon;
    case EnvironmentType.EdgeAgentOnDocker:
      return Cloud;
    case EnvironmentType.AgentOnKubernetes:
    case EnvironmentType.EdgeAgentOnKubernetes:
    case EnvironmentType.KubernetesLocal:
      return Kube;
    case EnvironmentType.AgentOnDocker:
    case EnvironmentType.Docker:
      if (containerEngine === ContainerEngine.Podman) {
        return PodmanIcon;
      }
      return swarm ? DockerSwarmIcon : DockerIcon;
    default:
      throw new Error(`type ${type}-${EnvironmentType[type]} is not supported`);
  }
}

export type IconSize = 'sm' | 'md';

export const IconSizeClass: Record<IconSize, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
};
